/**
 * @module Section-Blog-Box
 */

import { NavLink } from "react-router-dom";
import { TypeBlog } from "../../@types/reusableTypes";
import { navUrl } from "../../../globals/common-functions";
import { BLOG_BOX, route } from "../../../globals/constants";
import AvertImage from "../../../globals/elements/avertimg";
import AvertAudioPlayer from "../../../globals/elements/audio-player";

/**
 * 
 * @param props : Data supplied to display a single blog
 * @returns Markup for single blog box (of type image/audio/video etc.)
 */
const SectionBlogsCommonBox = (props: {
    _data: TypeBlog
}) => {
    const _item = props._data;
    
    // Calculate reading time based on title length (simple estimation)
    const estimateReadingTime = (text: string) => {
        const wordsPerMinute = 200;
        const wordCount = text.split(' ').length;
        const minutes = Math.max(1, Math.ceil(wordCount / wordsPerMinute));
        return `${minutes} min read`;
    };

    // Helper function to create blog detail URL with ID
    const getBlogDetailUrl = (blogId: string) => {
        return `/blog/${blogId}`;
    };

    return (
        <div className="av-lt-blog" style={{
            background: '#fff',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            border: '1px solid #f0f0f0'
        }}
        onMouseOver={e => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
        }}
        onMouseOut={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
        }}>
            <div className="wt-post-media" style={{ position: 'relative' }}>
                {
                    (_item.type === BLOG_BOX.IMAGE && _item.image) &&
                    <NavLink to={_item.id ? getBlogDetailUrl(_item.id) : navUrl.blogs(route.blogs.DETAIL)}>
                        <AvertImage 
                            src={_item.image!} 
                            alt={_item.title || ""} 
                            style={{
                                width: '100%',
                                height: '240px',
                                objectFit: 'cover'
                            }}
                        /> 
                    </NavLink>
                }

                {
                    (_item.type === BLOG_BOX.AUDIO && _item.audio) &&
                    <div className="app-cover-wrap">
                        <div id="app-cover">
                            <div id="bg-layer" />
                            <AvertAudioPlayer _playlist={_item.audio!} />
                        </div>
                    </div>
                }

                {
                    (_item.type === BLOG_BOX.VIDEO && _item.video) &&
                    <>
                        <NavLink to={_item.id ? getBlogDetailUrl(_item.id) : navUrl.blogs(route.blogs.DETAIL)}>
                            <AvertImage 
                                src={_item.video!.thumb} 
                                alt={_item.title || ""}
                                style={{
                                    width: '100%',
                                    height: '240px',
                                    objectFit: 'cover'
                                }}
                            /> 
                        </NavLink>
                        <div className="av-vid-btn-wrap">
                            <a href={_item.video!.link} className="mfp-video av-vid-btn btn-animation">
                                <i className="fa fa-play" />
                            </a>
                        </div>
                    </>
                }

                {
                    _item.type !== BLOG_BOX.AUDIO &&
                    <div className="post-date" style={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                        background: '#4557F6',
                        color: '#fff',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(69, 87, 246, 0.3)'
                    }}>
                        <span className="date" style={{ fontSize: '18px', fontWeight: '700', display: 'block' }}>{_item.day}</span>
                        <span className="month" style={{ fontSize: '12px', textTransform: 'uppercase' }}>{_item.month}</span>
                    </div>
                }

                {/* Reading Time Badge */}
                <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500'
                }}>
                    {estimateReadingTime(_item.title)}
                </div>
            </div>
            
            <div className="av-post-info" style={{ padding: '24px' }}>
                <div className="av-post-head" style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div className="post-categories">
                            <NavLink 
                                to={navUrl.blogs(route.blogs.BLOGS)}
                                style={{
                                    background: '#e8ecff',
                                    color: '#4557F6',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    textDecoration: 'none',
                                    textTransform: 'uppercase'
                                }}
                            >
                                {_item.category}
                            </NavLink>
                        </div>
                    </div>
                    
                    <div className="post-author" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '14px',
                        color: '#666',
                        marginTop: '-11px'
                    }}>
                        {/* Author Avatar */}
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #4557F6 0%, #2d3ca7 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: '600'
                        }}>
                            {_item.author.charAt(0).toUpperCase()}
                        </div>
                        <span>
                            By <NavLink 
                                to={navUrl.blogs(route.blogs.BLOGS)}
                                style={{
                                    color: '#4557F6',
                                    textDecoration: 'none',
                                    fontWeight: '600'
                                }}
                            >
                                {_item.author}
                            </NavLink>
                        </span>
                    </div>
                </div>
                
                <h3 className="post-title" style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '1.4',
                    marginBottom: '16px',
                    color: '#1A1424'
                }}>
                    <NavLink 
                        to={_item.id ? getBlogDetailUrl(_item.id) : navUrl.blogs(route.blogs.DETAIL)}
                        style={{
                            color: '#1A1424',
                            textDecoration: 'none',
                            transition: 'color 0.3s ease'
                        }}
                        onMouseOver={e => e.currentTarget.style.color = '#4557F6'}
                        onMouseOut={e => e.currentTarget.style.color = '#1A1424'}
                    >
                        {_item.title}
                    </NavLink>
                </h3>
                
                <div className="av-post-ftr" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                }}>
                    <NavLink 
                        to={_item.id ? getBlogDetailUrl(_item.id) : navUrl.blogs(route.blogs.DETAIL)} 
                        className="site-button button-sm"
                        style={{
                            background: '#4557F6',
                            color: '#fff',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={e => {
                            e.currentTarget.style.background = '#2d3ca7';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseOut={e => {
                            e.currentTarget.style.background = '#4557F6';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        Read More 
                        <i className="feather feather-arrow-right" style={{ fontSize: '14px' }} />
                    </NavLink>
                    
                    <div className="av-post-atglance" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div className="post-comment" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            color: '#999'
                        }}>
                            <i className="feather feather-message-square" />
                            <NavLink 
                                to={_item.id ? getBlogDetailUrl(_item.id) : navUrl.blogs(route.blogs.DETAIL)}
                                style={{ color: '#999', textDecoration: 'none' }}
                            >
                                {_item.comments}
                            </NavLink>
                        </div>
                        <div className="post-view" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            color: '#999'
                        }}>
                            <i className="feather feather-eye" />{_item.views}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SectionBlogsCommonBox;